import { useEffect, useRef, useState } from "react";
import { Compass, Send, AlertCircle, Sparkles } from "lucide-react";
import { ApiService } from "../../services/api";
import type { CoachChatMessage } from "../../types/navigator";

// Plan-sonrası koç sohbeti (K2) — öğrenci süreç hakkında konuşur:
// "salı çalışamıyorum", "günde 60 dakikaya düşelim", "nasıl gidiyorum?".
// Koç plan durumunu bilerek cevap verir; gün/dakika ve hedef tarih değişikliği
// profile işlenir, plan revizyonu "Planı güncelle" düğmesiyle öğrencinin onayına bağlıdır.

interface Props {
  onPlanRevised: () => void;
}

export default function CoachChat({ onPlanRevised }: Props) {
  const [messages, setMessages] = useState<CoachChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revisionPending, setRevisionPending] = useState(false);
  const [revising, setRevising] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollDown = () => bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  // Tanışma sohbetiyle aynı kural: sunucu son 20 mesajla çalışır, fazlasını göndermek isteği 400'e düşürür.
  const SEND_WINDOW = 20;

  const send = async (history: CoachChatMessage[]) => {
    setSending(true);
    setError(null);
    try {
      const res = await ApiService.coachPlanChat(history.slice(-SEND_WINDOW));
      setMessages([...history, { role: "assistant", content: res.reply }]);
      setRevisionPending(res.revisionPending);
    } catch (e: any) {
      setError(e?.message || "Koç şu an yanıt veremiyor");
    } finally {
      setSending(false);
      setTimeout(scrollDown, 50);
    }
  };

  // Açılış: kayıtlı plan sohbeti varsa kaldığı yerden; yoksa koç durumu bilerek selam verir.
  useEffect(() => {
    (async () => {
      try {
        const res = await ApiService.getCoachProfile();
        const saved = res.profile?.metadata?.coachChatMessages;
        if (Array.isArray(saved) && saved.length) {
          setMessages(saved);
          setRevisionPending(res.profile?.metadata?.coachChatRevisionPending === true);
          setTimeout(scrollDown, 50);
          return;
        }
      } catch {
        /* profil okunamazsa normal akışla başla */
      }
      send([]);
    })();
    // eslint-disable-next-line
  }, []);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const next: CoachChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setTimeout(scrollDown, 50);
    await send(next);
  };

  const revisePlan = async () => {
    setRevising(true);
    setError(null);
    try {
      await ApiService.createCoachPlan();
      setRevisionPending(false);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Planını yeni programına göre güncelledim ✅ Takvimini Planım sekmesinden görebilirsin." },
      ]);
      onPlanRevised();
      setTimeout(scrollDown, 50);
    } catch (e: any) {
      setError(e?.message || "Plan güncellenemedi");
    } finally {
      setRevising(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col px-4 py-5 sm:px-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-blue-100 text-blue-600">
          <Compass className="h-6 w-6" />
        </div>
        <div>
          <p className="text-base font-semibold text-gray-900">Koçunla konuş</p>
          <p className="text-xs text-gray-500">Programın değişti mi, nasıl gittiğini mi merak ediyorsun — buradayım.</p>
        </div>
      </div>

      <div className="mb-3 min-h-[280px] space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                m.role === "user"
                  ? "bg-blue-600 text-white"
                  : "border border-gray-200 bg-gray-50 text-gray-800"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-blue-600" />
              <span className="text-xs text-gray-500">koç yazıyor…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {revisionPending && (
        <button
          onClick={revisePlan}
          disabled={revising}
          className="mb-3 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
        >
          {revising ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
              Planın güncelleniyor…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" /> Planı güncelle
            </>
          )}
        </button>
      )}

      <form onSubmit={submit} className="flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending}
          placeholder="Koçuna yaz…"
          className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none transition-colors focus:border-blue-400"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          aria-label="Gönder"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
